import { Component } from "@theme/component";

const LOCALE_PARAM = "locale";
const CURRENCY_PARAM = "currency";

/**
 * @extends {Component<{
 *   form: HTMLFormElement,
 *   countryInput: HTMLInputElement,
 *   languageInput: HTMLInputElement,
 *   marketData: HTMLScriptElement
 * }>}
 */
class LocalizationUrlParamsComponent extends Component {
  requiredRefs = ["form", "countryInput", "languageInput", "marketData"];

  /** @type {Map<string, string> | null} */
  #countryMarketIndex = null;

  connectedCallback() {
    super.connectedCallback();
    requestAnimationFrame(() => this.#applyUrlParams());
  }

  /**
   * @returns {{
   *   currentCountry: string,
   *   currentLanguage: string,
   *   currentCurrency: string,
   *   currentHost: string,
   *   shopInternalDomain: string,
   *   availableCountryCodes: string[],
   *   countries: Array<{ isoCode: string, currency: string, languages: string[] }>
   * } | null}
   */
  #readSessionData() {
    const { marketData } = this.refs;

    if (!marketData?.textContent) {
      return null;
    }

    try {
      return JSON.parse(marketData.textContent);
    } catch {
      return null;
    }
  }

  /**
   * @param {string} raw
   * @returns {string | null}
   */
  #hostnameFromStoreUrl(raw) {
    if (!raw || typeof raw !== "string") {
      return null;
    }

    const trimmed = raw.trim();

    if (!trimmed) {
      return null;
    }

    const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

    try {
      return new URL(withScheme).hostname;
    } catch {
      return null;
    }
  }

  /**
   * @param {unknown} languageField
   * @returns {Array<{ iso_code: string, name: string, url?: string }>}
   */
  #languageEntriesFromStore(languageField) {
    if (!languageField) {
      return [];
    }

    if (Array.isArray(languageField)) {
      // @ts-expect-error
      return languageField
        .map((entry) => {
          if (entry && typeof entry === "object" && "code" in entry) {
            const row = /** @type {{ code?: string, name?: string, url?: string }} */ (entry);

            return {
              iso_code: String(row.code || "").trim(),
              name: String(row.name || row.code || "").trim(),
              url: row.url ? String(row.url).trim() : undefined,
            };
          }

          const code = String(entry).trim();

          return code ? { iso_code: code, name: code } : null;
        })
        .filter(Boolean);
    }

    if (typeof languageField === "string") {
      // @ts-expect-error
      return languageField
        .split(",")
        .map((code) => {
          const trimmed = code.trim();

          return trimmed ? { iso_code: trimmed, name: trimmed } : null;
        })
        .filter(Boolean);
    }

    return [];
  }

  /**
   * @param {string | undefined} rawCurrency
   * @returns {string}
   */
  #storeCurrency(rawCurrency) {
    if (!rawCurrency) {
      return "";
    }

    const first = String(rawCurrency).split(",")[0];

    return first ? first.trim().toUpperCase() : "";
  }

  /**
   * @param {string} isoRaw
   * @returns {string}
   */
  #storeFormCountryValue(isoRaw) {
    const trimmed = String(isoRaw || "").trim();

    if (!trimmed) {
      return "";
    }

    if (trimmed.includes(",")) {
      return trimmed
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean)
        .join("-");
    }

    return trimmed;
  }

  /**
   * @param {string} countryCode
   * @returns {string | null}
   */
  #findMarketKeyForCountry(countryCode) {
    if (!this.#countryMarketIndex) {
      this.#countryMarketIndex = new Map();
      try {
        let map = /** @type {any} */ (window).OtterVars?.MarketSettings?.countryToMarketMap;

        // Unroll double-encoded JSON strings safely
        while (typeof map === 'string') {
          map = JSON.parse(map);
        }

        if (map && typeof map === 'object') {
          for (const [key, value] of Object.entries(map)) {
            String(value).split(',').forEach((code) => {
              const iso = code.trim().toUpperCase();
              if (iso) this.#countryMarketIndex.set(iso, key);
            });
          }
        }
      } catch (e) {
        console.error('Market Key Not Found:', e);
      }
    }

    const needle = String(countryCode).trim().toUpperCase();
    return this.#countryMarketIndex.get(needle) ?? null;
  }

  /**
   * @param {{
   *   currentHost: string,
   *   shopInternalDomain: string,
   *   availableCountryCodes: string[],
   *   currentCurrency: string
   * }} session
   * @param {string} hostname
   * @param {string} isoRaw
   * @param {string} currency
   * @returns {boolean}
   */
  #isStoreAvailableOnHost(session, hostname, isoRaw, currency) {
    if (!hostname) {
      return false;
    }

    const host = session.currentHost?.toLowerCase();
    const shopHost = session.shopInternalDomain?.toLowerCase();
    const storeHost = hostname.toLowerCase();

    if (storeHost !== host && storeHost !== shopHost) {
      return false;
    }

    const available = (session.availableCountryCodes || []).map((c) => c.toUpperCase());

    if (!isoRaw.includes(",")) {
      const iso = isoRaw.trim().toUpperCase();

      if (available.includes(iso)) {
        return true;
      }

      const marketKey = this.#findMarketKeyForCountry(iso);

      if (marketKey && isoRaw.trim().toUpperCase() === marketKey) {
        return true;
      }
    }

    const currentCurrency = (session.currentCurrency || "").toUpperCase();

    return Boolean(currency && currentCurrency && currency === currentCurrency);
  }

  /**
   * @param {Record<string, unknown>} raw
   * @param {{
   *   currentHost: string,
   *   shopInternalDomain: string,
   *   availableCountryCodes: string[],
   *   currentCurrency: string
   * }} session
   * @returns {{
   *   isoRaw: string,
   *   formCountryValue: string,
   *   isComposite: boolean,
   *   hostname: string | null,
   *   currency: string,
   *   languages: Array<{ iso_code: string, name: string, url?: string }>,
   *   isAvailable: boolean,
   *   url?: string
   * } | null}
   */
  #normalizeRoutingStore(raw, session) {
    const isoRaw = String(raw.isoCode || "").trim();

    if (!isoRaw) {
      return null;
    }

    const hostname = this.#hostnameFromStoreUrl(String(raw.url || ""));
    const currency = this.#storeCurrency(String(raw.currency || ""));

    return {
      isoRaw,
      formCountryValue: this.#storeFormCountryValue(isoRaw),
      isComposite: isoRaw.includes(","),
      hostname,
      currency,
      languages: this.#languageEntriesFromStore(raw.language),
      isAvailable: this.#isStoreAvailableOnHost(session, hostname || "", isoRaw, currency),
      url: raw.url ? String(raw.url) : undefined,
    };
  }

  /**
   * @param {{
   *   currentHost: string,
   *   shopInternalDomain: string,
   *   availableCountryCodes: string[],
   *   currentCurrency: string
   * }} session
   * @returns {Array<{
   *   isoRaw: string,
   *   formCountryValue: string,
   *   isComposite: boolean,
   *   hostname: string | null,
   *   currency: string,
   *   languages: Array<{ iso_code: string, name: string, url?: string }>,
   *   isAvailable: boolean,
   *   url?: string
   * }>}
   */
  #getRoutingStores(session) {
    const routing = /** @type {Window & { marketRouting?: { stores?: unknown[] } }} */ (window).marketRouting;
    const rawStores = routing?.stores;

    if (!Array.isArray(rawStores) || rawStores.length === 0) {
      return [];
    }

    // @ts-expect-error
    return rawStores
      .map((store) => this.#normalizeRoutingStore(/** @type {Record<string, unknown>} */(store), session))
      .filter(Boolean);
  }

  /**
   * @param {string} isoRaw
   * @param {string} countryNeedle
   * @returns {boolean}
   */
  #storeMatchesCountryIso(isoRaw, countryNeedle) {
    const upper = countryNeedle.toUpperCase();

    if (!isoRaw.includes(",")) {
      return isoRaw.trim().toUpperCase() === upper;
    }

    return isoRaw
      .split(",")
      .map((part) => part.trim().toUpperCase())
      .includes(upper);
  }

  /**
   * @param {{
   *   isoRaw: string,
   *   formCountryValue: string,
   *   isComposite: boolean,
   *   hostname: string | null,
   *   currency: string,
   *   languages: Array<{ iso_code: string, name: string, url?: string }>,
   *   isAvailable: boolean,
   *   url?: string
   * }} store
   * @param {string} language
   * @returns {string | null}
   */
  #resolveLanguageCode(store, language) {
    const needle = language.toLowerCase();
    const match = store.languages.find((lang) => lang.iso_code.toLowerCase() === needle);

    if (match) {
      return match.iso_code;
    }

    if (store.languages.length > 0) {
      return store.languages?.[0]?.iso_code ?? null;
    }

    return language;
  }

  /**
   * @param {{
   *   currentCountry: string,
   *   currentLanguage: string,
   *   currentCurrency: string
   * }} session
   * @param {Array<{
   *   isoRaw: string,
   *   formCountryValue: string,
   *   currency: string,
   *   languages: Array<{ iso_code: string, name: string, url?: string }>,
   *   isAvailable: boolean,
   *   hostname: string | null,
   *   url?: string
   * }>} stores
   * @returns {{
   *   isoRaw: string,
   *   formCountryValue: string,
   *   currency: string,
   *   languages: Array<{ iso_code: string, name: string, url?: string }>,
   *   isAvailable: boolean,
   *   hostname: string | null,
   *   url?: string
   * } | undefined}
   */
  #findActiveStore(session, stores) {
    const available = stores.filter((s) => s.isAvailable);
    const exact = available.find(
      (s) => !s.isoRaw.includes(",") && s.isoRaw.trim().toUpperCase() === session.currentCountry.toUpperCase(),
    );

    if (exact) {
      return exact;
    }

    const marketKey = this.#findMarketKeyForCountry(session.currentCountry);

    if (marketKey) {
      const byMarket = available.find(
        (s) => !s.isoRaw.includes(",") && s.isoRaw.trim().toUpperCase() === marketKey.toUpperCase(),
      );

      if (byMarket) {
        return byMarket;
      }
    }

    const upperCurrency = (session.currentCurrency || "").toUpperCase();

    return available.find((s) => s.currency === upperCurrency);
  }

  /**
   * @param {{ language: string, country: string | null }} parsed
   * @param {{
   *   currentCountry: string,
   *   currentLanguage: string,
   *   currentCurrency: string,
   *   availableCountryCodes: string[]
   * }} session
   * @param {Array<{
   *   isoRaw: string,
   *   formCountryValue: string,
   *   currency: string,
   *   languages: Array<{ iso_code: string, name: string, url?: string }>,
   *   isAvailable: boolean,
   *   hostname: string | null,
   *   url?: string
   * }>} stores
   * @returns {{
   *   countryCode: string,
   *   languageCode: string,
   *   store: {
   *     formCountryValue: string,
   *     currency: string,
   *     isAvailable: boolean,
   *     languages: Array<{ iso_code: string, name: string, url?: string }>,
   *     url?: string
   *   }
   * } | null}
   */
  #resolveLocaleFromStores(parsed, session, stores) {
    const onHost = stores.filter((s) => s.hostname);
    const available = onHost.filter((s) => s.isAvailable);

    /** @type {typeof stores[0] | undefined} */
    let store;

    if (parsed.country) {
      const marketKey = this.#findMarketKeyForCountry(parsed.country);

      store =
        available.find(
          (s) =>
            !s.isoRaw.includes(",") &&
            (s.isoRaw.trim().toUpperCase() === parsed.country?.toUpperCase() ||
              (marketKey && s.isoRaw.trim().toUpperCase() === marketKey.toUpperCase())),
        ) || onHost.find((s) => this.#storeMatchesCountryIso(s.isoRaw, parsed.country || ""));

      if (!store) {
        return null;
      }
    } else if (parsed.language) {
      store =
        available.find((s) =>
          s.languages.some((lang) => lang.iso_code.toLowerCase() === parsed.language.toLowerCase()),
        ) ||
        onHost.find((s) => s.languages.some((lang) => lang.iso_code.toLowerCase() === parsed.language.toLowerCase()));

      if (!store) {
        return null;
      }
    } else {
      return null;
    }

    // @ts-expect-error
    const languageCode = this.#resolveLanguageCode(store, parsed.language || session.currentLanguage);

    return {
      countryCode: store.formCountryValue,
      languageCode: languageCode ?? "",
      store,
    };
  }

  /**
   * @param {string} currency
   * @param {string} languageCode
   * @param {{
   *   currentCountry: string,
   *   currentCurrency: string
   * }} session
   * @param {Array<{
   *   isoRaw: string,
   *   formCountryValue: string,
   *   currency: string,
   *   languages: Array<{ iso_code: string, name: string, url?: string }>,
   *   isAvailable: boolean,
   *   hostname: string | null,
   *   url?: string
   * }>} stores
   * @returns {{
   *   countryCode: string,
   *   languageCode: string,
   *   store: {
   *     formCountryValue: string,
   *     currency: string,
   *     isAvailable: boolean,
   *     languages: Array<{ iso_code: string, name: string, url?: string }>,
   *     url?: string
   *   }
   * } | null}
   */
  #resolveCurrencyFromStores(currency, languageCode, session, stores) {
    const upper = currency.toUpperCase();
    const available = stores.filter((s) => s.isAvailable);

    let store =
      available.find(
        (s) =>
          !s.isoRaw.includes(",") &&
          s.isoRaw.trim().toUpperCase() === session.currentCountry.toUpperCase() &&
          s.currency === upper,
      ) || available.find((s) => s.currency === upper);

    if (!store) {
      store = stores.find((s) => s.currency === upper);
    }

    if (!store) {
      return null;
    }

    let resolvedLanguage = languageCode;

    if (!store.languages.some((lang) => lang.iso_code.toLowerCase() === resolvedLanguage.toLowerCase())) {
      resolvedLanguage = store.languages[0]?.iso_code ?? resolvedLanguage;
    }

    return {
      countryCode: store.formCountryValue,
      languageCode: resolvedLanguage,
      store,
    };
  }

  /**
   * @param {string} language
   * @param {{ isoCode: string, currency: string, languages: string[] }} country
   * @returns {boolean}
   */
  #countrySupportsLanguage(language, country) {
    return country.languages.some((code) => code.toLowerCase() === language.toLowerCase());
  }

  /**
   * @param {{
   *   currentCountry: string,
   *   countries: Array<{ isoCode: string, currency: string, languages: string[] }>
   * }} session
   * @param {string} isoCode
   * @returns {{ isoCode: string, currency: string, languages: string[] } | undefined}
   */
  #findCountryFallback(session, isoCode) {
    return session.countries.find((c) => c.isoCode.toUpperCase() === isoCode.toUpperCase());
  }

  /**
   * @param {{ language: string, country: string | null }} parsed
   * @param {{
   *   currentCountry: string,
   *   currentLanguage: string,
   *   countries: Array<{ isoCode: string, currency: string, languages: string[] }>
   * }} session
   * @returns {{ countryCode: string, languageCode: string } | null}
   */
  #resolveLocaleFromCountries(parsed, session) {
    let { currentCountry: countryCode, currentLanguage: languageCode } = session;

    if (parsed.country) {
      const country = this.#findCountryFallback(session, parsed.country);

      if (!country) {
        return null;
      }

      countryCode = country.isoCode;

      if (!this.#countrySupportsLanguage(parsed.language, country)) {
        languageCode = country.languages[0] ?? languageCode;
      } else {
        languageCode =
          country.languages.find((code) => code.toLowerCase() === parsed.language.toLowerCase()) ?? parsed.language;
      }
    } else if (parsed.language) {
      const current = this.#findCountryFallback(session, countryCode);

      if (current && this.#countrySupportsLanguage(parsed.language, current)) {
        languageCode =
          current.languages.find((code) => code.toLowerCase() === parsed.language.toLowerCase()) ?? parsed.language;
      } else {
        const fallback = session.countries.find((c) => this.#countrySupportsLanguage(parsed.language, c));

        if (!fallback) {
          return null;
        }

        countryCode = fallback.isoCode;
        languageCode =
          fallback.languages.find((code) => code.toLowerCase() === parsed.language.toLowerCase()) ?? parsed.language;
      }
    }

    return { countryCode, languageCode };
  }

  /**
   * @param {string} currency
   * @param {string} languageCode
   * @param {{
   *   currentCountry: string,
   *   countries: Array<{ isoCode: string, currency: string, languages: string[] }>
   * }} session
   * @returns {{ countryCode: string, languageCode: string } | null}
   */
  #resolveCurrencyFromCountries(currency, languageCode, session) {
    const upper = currency.toUpperCase();
    const matches = session.countries.filter((c) => c.currency.toUpperCase() === upper);

    if (!matches.length) {
      return null;
    }

    const current = matches.find((c) => c.isoCode === session.currentCountry);
    const country = current || matches.find((c) => this.#countrySupportsLanguage(languageCode, c)) || matches[0];

    let resolvedLanguage = languageCode;

    // @ts-expect-error
    if (!this.#countrySupportsLanguage(resolvedLanguage, country)) {
      resolvedLanguage = country?.languages?.[0] ?? resolvedLanguage;
    }

    return { countryCode: country?.isoCode ?? "", languageCode: resolvedLanguage };
  }

  /**
   * @param {{
   *   formCountryValue: string,
   *   currency: string,
   *   isAvailable: boolean,
   *   languages: Array<{ iso_code: string, name: string, url?: string }>
   * }} store
   * @param {string} languageCode
   * @param {string} [currencyParam]
   * @returns {boolean}
   */
  #redirectToCrossStore(store, languageCode, currencyParam) {
    if (store.isAvailable) {
      return false;
    }

    const lang = store.languages.find((entry) => entry.iso_code.toLowerCase() === languageCode.toLowerCase());
    const redirectUrl = lang?.url;

    if (!redirectUrl) {
      return false;
    }

    const currentUrl = new URL(window.location.href);
    const normalizedBaseUrl = /^https?:\/\//i.test(redirectUrl) ? redirectUrl : `https://${redirectUrl}`;
    const baseUrl = new URL(normalizedBaseUrl);
    const basePath = baseUrl.pathname.replace(/\/+$/, "");

    const localePrefix = /** @type {any} */ (window).OtterVars?.MarketSettings?.rootUrl?.replace(/\/+$/, "") || "";
    let currentPath = currentUrl.pathname;

    if (localePrefix.length > 1 && currentPath.toLowerCase().startsWith(localePrefix.toLowerCase())) {
      currentPath = currentPath.slice(localePrefix.length);
    }

    currentPath = currentPath.replace(/^\/+/, "");

    const combinedPath = [basePath, currentPath].filter(Boolean).join("/");
    const targetPathname = combinedPath ? (combinedPath.startsWith("/") ? combinedPath : `/${combinedPath}`) : "/";
    const url = new URL(baseUrl.origin);
    url.pathname = targetPathname;
    url.search = currentUrl.search;
    url.hash = currentUrl.hash;

    if (currencyParam) {
      url.searchParams.set(CURRENCY_PARAM, currencyParam.toUpperCase());
    }

    window.location.href = url.toString();

    return true;
  }

  /**
   * @param {string} name
   * @param {string} value
   * @param {{ days?: number }} [options]
   */
  #setCookie(name, value, options = {}) {
    const { days } = options;
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (typeof days === "number") {
      const date = new Date();
      date.setTime(date.getTime() + days * 864e5);
      cookieString += `; expires=${date.toUTCString()}`;
    }

    cookieString += "; path=/; samesite=Lax";
    document.cookie = cookieString;
  }

  /**
   * @param {string} value
   * @returns {{ language: string, country: string | null } | null}
   */
  #parseLocaleParam(value) {
    const trimmed = value.trim();

    if (!trimmed) {
      return null;
    }

    const normalized = trimmed.replace(/_/g, "-");
    const parts = normalized.split("-").filter(Boolean);

    if (parts.length === 0) {
      return null;
    }

    if (parts.length === 1) {
      return { language: parts[0]?.toLowerCase() ?? "", country: null };
    }

    return {
      language: parts[0]?.toLowerCase() ?? "",
      country: parts[parts.length - 1]?.toUpperCase() ?? "",
    };
  }

  #updateReturnTo() {
    const { form } = this.refs;
    const url = new URL(window.location.href);
    url.searchParams.delete(LOCALE_PARAM);
    url.searchParams.delete(CURRENCY_PARAM);
    const returnTo = `${url.pathname}${url.search}${url.hash}`;

    let input = form.querySelector('input[name="return_to"]');

    if (!input) {
      input = document.createElement("input");
      // @ts-expect-error
      input.type = "hidden";
      // @ts-expect-error
      input.name = "return_to";
      form.appendChild(input);
    }

    // @ts-expect-error
    input.value = returnTo;
  }

  #applyUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const localeParam = params.get(LOCALE_PARAM);
    const currencyParam = params.get(CURRENCY_PARAM);

    if (!localeParam && !currencyParam) {
      return;
    }

    const session = this.#readSessionData();

    if (!session) {
      return;
    }

    const { countryInput, languageInput, form } = this.refs;
    const routingStores = this.#getRoutingStores(session);
    const useRouting = routingStores.length > 0;

    let countryCode = session.currentCountry;
    let languageCode = session.currentLanguage;
    /** @type {{ formCountryValue: string, currency: string, isAvailable: boolean, languages: Array<{ iso_code: string, name: string, url?: string }>, url?: string } | null} */
    let resolvedStore = null;

    if (localeParam) {
      const parsed = this.#parseLocaleParam(localeParam);

      if (!parsed) {
        return;
      }

      if (useRouting) {
        const resolved = this.#resolveLocaleFromStores(parsed, session, routingStores);

        if (!resolved) {
          return;
        }

        countryCode = resolved.countryCode;
        languageCode = resolved.languageCode;
        resolvedStore = resolved.store;
      } else {
        const resolved = this.#resolveLocaleFromCountries(parsed, session);

        if (!resolved) {
          return;
        }

        countryCode = resolved.countryCode;
        languageCode = resolved.languageCode;
      }
    }

    if (currencyParam) {
      if (useRouting) {
        const resolved = this.#resolveCurrencyFromStores(currencyParam, languageCode, session, routingStores);

        if (!resolved) {
          return;
        }

        countryCode = resolved.countryCode;
        languageCode = resolved.languageCode;
        resolvedStore = resolved.store;
      } else {
        const resolved = this.#resolveCurrencyFromCountries(currencyParam, languageCode, session);

        if (!resolved) {
          return;
        }

        countryCode = resolved.countryCode;
        languageCode = resolved.languageCode;
      }
    }

    if (resolvedStore && this.#redirectToCrossStore(resolvedStore, languageCode, currencyParam || undefined)) {
      return;
    }

    const activeStore = useRouting ? this.#findActiveStore(session, routingStores) : null;
    const activeCountryValue = activeStore?.formCountryValue ?? session.currentCountry;
    const activeCurrency = activeStore?.currency ?? session.currentCurrency;

    const marketMatches =
      countryCode === activeCountryValue && languageCode.toLowerCase() === session.currentLanguage.toLowerCase();
    const currencyMatches = !currencyParam || activeCurrency.toUpperCase() === currencyParam.toUpperCase();

    if (!useRouting) {
      const country = this.#findCountryFallback(session, countryCode);
      const fallbackCurrency = country?.currency ?? session.currentCurrency;
      const fallbackMatches = !currencyParam || fallbackCurrency.toUpperCase() === currencyParam.toUpperCase();

      if (
        countryCode === session.currentCountry &&
        languageCode.toLowerCase() === session.currentLanguage.toLowerCase() &&
        fallbackMatches
      ) {
        return;
      }
    } else if (marketMatches && currencyMatches) {
      return;
    }

    countryInput.value = countryCode;
    languageInput.value = languageCode;

    this.#updateReturnTo();
    this.#setCookie("declined_redirect", "true", { days: 30 });
    form.submit();
  }
}

if (!customElements.get("localization-url-params-component")) {
  customElements.define("localization-url-params-component", LocalizationUrlParamsComponent);
}
